<template>
  <div class="pa-1 mt-4">
    <!-- Summary Cards -->
    <v-row class="mb-4">
      <v-col cols="12" sm="4">
        <v-card outlined class="ordera-box-shadow">
          <v-card-text>
            <div class="text-center">
              <div class="text-caption text--secondary mb-2">
                {{ $t("company_topups_total") }}
              </div>
              <div class="text-h5 blue--text font-weight-bold">
                {{ formatCurrency(companyTotal) }}
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" sm="4">
        <v-card outlined class="ordera-box-shadow">
          <v-card-text>
            <div class="text-center">
              <div class="text-caption text--secondary mb-2">
                {{ $t("employee_topups_total") }}
              </div>
              <div class="text-h5 grey--text font-weight-bold">
                {{ formatCurrency(employeeTotal) }}
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" sm="4">
        <v-card outlined class="ordera-box-shadow">
          <v-card-text>
            <div class="text-center">
              <div class="text-caption text--secondary mb-2">
                {{ $t("total_topups_amount") }}
              </div>
              <div class="text-h5 primary--text font-weight-bold">
                {{ formatCurrency(grandTotal) }}
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Desktop View -->
    <v-data-table
      v-if="!device.isMobile"
      :headers="headers"
      :items="creditTopupsWithTotal"
      class="striped-table ordera-box-shadow"
      hide-default-footer
      disable-pagination
      mobile-breakpoint="0"
      :loading="loading"
    >
      <template v-slot:no-data>
        <v-container class="text-center py-12">
          <v-icon size="64" color="grey lighten-1" class="mb-4">mdi-cash-off</v-icon>
          <div class="text-h6 grey--text text--darken-1 mb-2">
            {{ $t("no_data") }}
          </div>
          <div class="text-body-2 grey--text">
            {{ $t("select_date_and_filters") }}
          </div>
        </v-container>
      </template>

      <template v-slot:item.num="{ item }">
        <span :class="item.isTotal ? 'font-weight-bold' : ''">
          {{ item.num }}
        </span>
      </template>

      <template v-slot:item.date="{ item }">
        <span :class="item.isTotal ? 'font-weight-bold' : ''">
          {{ item.isTotal ? $t("total").toUpperCase() : localeDate(item.date) }}
        </span>
      </template>

      <template v-slot:item.employee_id="{ item }">
        <span :class="item.isTotal ? 'font-weight-bold' : ''">
          {{ item.employee_id }}
        </span>
      </template>

      <template v-slot:item.employee_name="{ item }">
        <span :class="item.isTotal ? 'font-weight-bold' : ''">
          {{ item.employee_name }}
        </span>
      </template>

      <template v-slot:item.source="{ item }">
        <v-chip
          v-if="!item.isTotal"
          small
          :color="item.source === 'company' ? 'blue' : 'grey'"
        >
          {{ $t(item.source) }}
        </v-chip>
      </template>

      <template v-slot:item.amount="{ item }">
        <span
          :class="
            item.isTotal
              ? 'font-weight-bold primary--text'
              : 'font-weight-bold success--text'
          "
        >
          {{ item.isTotal ? "" : "+" }}{{ formatAmount(item.amount) }}
        </span>
      </template>

      <template v-slot:item.topup_type="{ item }">
        <span>{{ item.topup_type ? $t(item.topup_type) : "" }}</span>
      </template>

      <template v-slot:item.payment_method="{ item }">
        <span>{{ item.payment_method ? $t(item.payment_method) : "—" }}</span>
      </template>
    </v-data-table>

    <!-- Mobile View -->
    <mobile-data-cards
      v-else
      :headers="mobileHeaders"
      :items="creditTopups"
      :loading="loading"
      item-key="id"
      :fields-per-row="2"
      :bold-fields="['employee_name']"
      :show-row-number="false"
      class="px-3"
    >
      <template v-slot:item.date="{ item }">
        {{ localeDate(item.date) }}
      </template>
      <template v-slot:item.source="{ item }">
        <v-chip small :color="item.source === 'company' ? 'blue' : 'grey'">
          {{ $t(item.source) }}
        </v-chip>
      </template>
      <template v-slot:item.amount="{ item }">
        <span class="font-weight-bold success--text">
          +{{ formatAmount(item.amount) }}
        </span>
      </template>
      <template v-slot:item.topup_type="{ item }">
        <span>{{ item.topup_type ? $t(item.topup_type) : "" }}</span>
      </template>
      <template v-slot:item.payment_method="{ item }">
        <span>{{ item.payment_method ? $t(item.payment_method) : "—" }}</span>
      </template>
    </mobile-data-cards>
  </div>
</template>

<script>
import hasSnackbar from "@/mixins/hasSnackbar";
import hasTopupFilters from "@/mixins/report/hasTopupFilters";
import MobileDataCards from "@/components/global/MobileDataCards.vue";
import hasPrinting from "@/mixins/hasPrinting";
import hasReportPrinting from "@/mixins/report/hasReportPrinting";
import hasExcel from "@/mixins/hasExcel";

export default {
  name: "CompanyMultiRestaurantTabCreditTopups",
  components: {
    MobileDataCards,
  },
  mixins: [hasSnackbar, hasTopupFilters, hasPrinting, hasReportPrinting, hasExcel],
  data: () => ({
    loading: false,
  }),
  computed: {
    filters() {
      return this.$store.state.report.filters;
    },
    restaurants() {
      return this.$store.state.company?.restaurants || [];
    },
    creditTopups() {
      // Generate fake data for credit topups
      const topups = [];
      const employees = [
        "Marko Marković",
        "Ana Petrović",
        "Nikola Jovanović",
        "Milica Nikolić",
        "Stefan Đorđević",
      ];
      const sources = this.topupSources.map((s) => s.value);
      const topupTypes = this.topupTypes.map((t) => t.value);
      const paymentMethods = this.paymentMethods.map((p) => p.value);
      const amounts = [5, 10, 15, 20, 25, 50, 100];

      // Calculate number of days in selected date range
      const dateFrom = this.filters.dates?.from;
      const dateTo = this.filters.dates?.to;
      let dayCount = 7;

      if (dateFrom && dateTo) {
        const start = new Date(dateFrom);
        const end = new Date(dateTo);
        dayCount = Math.max(1, Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1);
      }

      // Generate 2-5 topups per day
      const topupsPerDay = Math.min(2 + Math.floor(Math.random() * 4), dayCount * 3);
      const totalTopups = topupsPerDay * Math.min(dayCount, 10); // Limit to avoid too many records

      for (let i = 0; i < totalTopups; i++) {
        const startDate = dateFrom ? new Date(dateFrom) : new Date();
        const endDate = dateTo ? new Date(dateTo) : new Date();
        const randomTime =
          startDate.getTime() + Math.random() * (endDate.getTime() - startDate.getTime());
        const randomDate = new Date(randomTime);

        const source = sources[Math.floor(Math.random() * sources.length)];
        const topupType =
          source === "company"
            ? topupTypes[0] // Dnevna subvencija for Firma
            : topupTypes[1 + Math.floor(Math.random() * (topupTypes.length - 1))]; // Other types for Radnik

        // Payment method: Automatska for Firma daily subsidy, random for others
        let paymentMethod;
        if (source === "company" && topupType === "daily_subsidy") {
          paymentMethod = "automatic";
        } else if (source === "employee") {
          paymentMethod =
            paymentMethods[1 + Math.floor(Math.random() * (paymentMethods.length - 1))];
        } else {
          // For other Firma topups, assign a random payment method
          paymentMethod =
            paymentMethods[Math.floor(Math.random() * paymentMethods.length)];
        }

        topups.push({
          id: i + 1,
          date: randomDate.toISOString().substr(0, 10),
          employee_id: 1000 + Math.floor(Math.random() * 9000),
          employee_name: employees[Math.floor(Math.random() * employees.length)],
          source: source,
          amount: amounts[Math.floor(Math.random() * amounts.length)],
          topup_type: topupType,
          payment_method: paymentMethod,
        });
      }

      // Sort by date descending
      let filteredTopups = topups.sort((a, b) => new Date(b.date) - new Date(a.date));

      // Apply source filter
      const sourceFilter = this.filters.topupSource || [];
      if (sourceFilter.length > 0) {
        filteredTopups = filteredTopups.filter((topup) =>
          sourceFilter.includes(topup.source)
        );
      }

      // Apply payment method filter
      const paymentMethodFilter = this.filters.paymentMethod || [];
      if (paymentMethodFilter.length > 0) {
        filteredTopups = filteredTopups.filter((topup) => {
          return (
            topup.payment_method && paymentMethodFilter.includes(topup.payment_method)
          );
        });
      }

      return filteredTopups;
    },
    creditTopupsWithTotal() {
      const topups = this.creditTopups.map((item, index) => ({
        ...item,
        num: index + 1,
        isTotal: false,
      }));

      // Add total row
      if (topups.length > 0) {
        const totalAmount = topups.reduce((sum, item) => sum + item.amount, 0);
        topups.push({
          id: "total",
          num: "",
          date: "",
          employee_id: "",
          employee_name: "",
          source: "",
          amount: totalAmount,
          topup_type: "",
          payment_method: "—",
          isTotal: true,
        });
      }

      return topups;
    },
    companyTotal() {
      return this.getTotalBySource("company");
    },
    employeeTotal() {
      return this.getTotalBySource("employee");
    },
    grandTotal() {
      return this.companyTotal + this.employeeTotal;
    },
    headers() {
      return [
        {
          text: this.$t("r_b"),
          value: "num",
          sortable: false,
          width: "80px",
          print: false,
        },
        { text: this.$t("date"), value: "date", sortable: true },
        { text: this.$t("user_id"), value: "employee_id", sortable: false },
        { text: this.$t("employee"), value: "employee_name", sortable: false },
        { text: this.$t("source"), value: "source", sortable: false, align: "center" },
        {
          text: this.$t("amount") + ` (${this.restaurantCurrency})`,
          value: "amount",
          sortable: false,
          align: "right",
        },
        { text: this.$t("topup_type"), value: "topup_type", sortable: false },
        {
          text: this.$t("payment_method"),
          value: "payment_method",
          sortable: false,
        },
      ];
    },
    mobileHeaders() {
      return [
        { text: this.$t("date"), value: "date" },
        { text: this.$t("employee_id"), value: "employee_id" },
        { text: this.$t("employee"), value: "employee_name" },
        { text: this.$t("source"), value: "source" },
        { text: this.$t("amount") + ` (${this.restaurantCurrency})`, value: "amount" },
        { text: this.$t("topup_type"), value: "topup_type" },
        { text: this.$t("payment_method"), value: "payment_method" },
      ];
    },
  },
  methods: {
    formatCurrency(amount) {
      return (parseFloat(amount) || 0).toFixed(2) + " " + this.restaurantCurrency;
    },
    formatAmount(amount) {
      return (parseFloat(amount) || 0).toFixed(2);
    },
    getTotalBySource(source) {
      return this.creditTopups
        .filter((t) => t.source === source)
        .reduce((sum, t) => sum + t.amount, 0);
    },
    getExportHeaders() {
      return this.headers.filter((h) => h.print !== false);
    },
    formatTopupForExport(item) {
      const exportItem = {};
      this.getExportHeaders().forEach((h) => {
        const v = item[h.value];
        if (h.value === "date") exportItem[h.value] = this.localeDate(v);
        else if (h.value === "source") exportItem[h.value] = this.$t(v);
        else if (h.value === "amount") exportItem[h.value] = this.formatAmount(v);
        else if (["topup_type", "payment_method"].includes(h.value))
          exportItem[h.value] = v ? this.$t(v) : h.value === "payment_method" ? "—" : "";
        else exportItem[h.value] = v || "";
      });
      return exportItem;
    },
  },
  watch: {
    isExcelDownloading() {
      if (!this.isExcelDownloading) return;
      this.downloadExcel({
        fileName: [this.$t("report"), this.$t("credit_topups")].join(" ") + ".xlsx",
        sheets: [
          {
            name: this.$t("credit_topups"),
            headers: this.getExportHeaders(),
            items: this.creditTopups.map((item) => this.formatTopupForExport(item)),
            title: [this.$t("report"), this.$t("credit_topups")].join(" "),
            subtitle: this.subtitle,
          },
        ],
      });
    },
    isPrinting(isPrinting) {
      if (!isPrinting) return;
      this.printDocument({
        tables: [
          {
            headers: this.getExportHeaders(),
            items: this.creditTopups.map((item) => this.formatTopupForExport(item)),
            showCaption: true,
          },
        ],
        title: [this.$t("report"), this.$t("credit_topups")].join(": "),
        subtitle: this.subtitle,
        hideDatetime: true,
      });
    },
  },
};
</script>
